import React, { useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { HiMenu } from "react-icons/hi";
import { FaUserCircle } from "react-icons/fa";
import { useLayout } from "../contexts/layoutContext";
import { NAV_ITEMS, LOGO_URL } from "../config/navigation";

const NavBar = ({ showDropdown, setShowDropdown, openModal, onLogout }) => {
  const { isDesktop, toggleSidebar, sidebarCollapsed } = useLayout();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const currentSection = useMemo(
    () => NAV_ITEMS.find((item) => item.route === location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown, setShowDropdown]);

  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  return (
    <header className="sticky top-0 z-20 flex w-full items-center justify-between gap-3 border-b border-blue-300 bg-blue-200 px-3 py-2 shadow-sm sm:px-4">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {isDesktop ? (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? "Expandir menú lateral" : "Contraer menú lateral"}
            aria-expanded={!sidebarCollapsed}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white bg-opacity-90 text-blue-800 shadow-sm transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
          >
            <HiMenu size={22} />
          </button>
        ) : (
          <img
            src={LOGO_URL}
            alt=""
            className="h-9 w-9 shrink-0 rounded-lg object-contain"
            aria-hidden
          />
        )}

        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-blue-900 sm:text-base">
            {currentSection?.shortLabel || currentSection?.label || "Muv Service"}
          </p>
          {!isDesktop && currentSection?.label && currentSection.label !== currentSection.shortLabel && (
            <p className="truncate text-xs text-blue-800 opacity-75">{currentSection.label}</p>
          )}
        </div>
      </div>

      <div className="relative shrink-0" ref={dropdownRef}>
        <button
          type="button"
          onClick={toggleDropdown}
          aria-label="Menú de usuario"
          aria-expanded={showDropdown}
          className="flex items-center rounded-full transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
        >
          <FaUserCircle className="h-10 w-10 text-blue-800" />
        </button>

        {showDropdown && (
          <div
            className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            role="menu"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setShowDropdown(false);
                openModal?.();
              }}
              className="w-full px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100"
            >
              Vista de perfil
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setShowDropdown(false);
                onLogout?.();
              }}
              className="w-full border-t border-gray-100 px-4 py-3 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
            >
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default NavBar;
